import { useEffect, useState } from "react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import { Building2, ExternalLink, Mail, Phone, Globe2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_BADGE = {
  active: { bg: "#DCFCE7", fg: "#166534", label: "Accepting books" },
  paused: { bg: "#FEF3C7", fg: "#92400E", label: "Paused" },
  not_accepting: { bg: "#FEE2E2", fg: "#991B1B", label: "Not accepting" },
};

function PublisherCard({ p }) {
  const badge = STATUS_BADGE[p.listing_status] || STATUS_BADGE.active;
  return (
    <article
      className="p-6 rounded-lg flex flex-col h-full"
      style={{ background: "white", border: "1px solid var(--bf-border)" }}
      data-testid={`publisher-card-${p.user_id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        {p.logo_url ? (
          <img src={p.logo_url} alt={p.company_name} className="w-12 h-12 rounded object-cover flex-shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded flex items-center justify-center flex-shrink-0" style={{ background: "var(--bf-surface)" }}>
            <Building2 className="w-6 h-6" style={{ color: "var(--bf-primary)" }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-xl leading-tight">{p.company_name}</h3>
          {p.tagline && <p className="text-xs mt-1" style={{ color: "var(--bf-text-muted)" }}>{p.tagline}</p>}
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: badge.bg, color: badge.fg }}>{badge.label}</span>
      </div>

      {p.description && <p className="text-sm mb-3 line-clamp-3">{p.description}</p>}

      {(p.services || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {p.services.slice(0, 6).map((s) => (
            <span key={s} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "var(--bf-surface)", color: "var(--bf-text-muted)" }}>
              {s.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      )}

      <dl className="text-xs space-y-1 mb-4 grid grid-cols-2 gap-x-3">
        {p.base_fee != null && (
          <>
            <dt style={{ color: "var(--bf-text-muted)" }}>Base fee</dt>
            <dd className="font-medium">{p.currency} {Number(p.base_fee).toLocaleString()}</dd>
          </>
        )}
        {p.turnaround && (
          <>
            <dt style={{ color: "var(--bf-text-muted)" }}>Turnaround</dt>
            <dd>{p.turnaround}</dd>
          </>
        )}
        {p.discount_percent != null && p.discount_percent > 0 && (
          <>
            <dt style={{ color: "var(--bf-text-muted)" }}>Discount</dt>
            <dd className="font-medium" style={{ color: "var(--bf-primary)" }}>{p.discount_percent}% off</dd>
          </>
        )}
      </dl>

      {p.discount_note && (
        <p className="text-xs p-2 rounded mb-3" style={{ background: "#FFEDD5", color: "#7C2D12" }} data-testid={`publisher-offer-${p.user_id}`}>
          {p.discount_note}
        </p>
      )}

      <div className="flex flex-wrap gap-3 text-xs mt-auto pt-3 border-t" style={{ borderColor: "var(--bf-border)", color: "var(--bf-text-muted)" }}>
        {p.contact_email && (<span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {p.contact_email}</span>)}
        {p.contact_phone && (<span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {p.contact_phone}</span>)}
        {p.contact_website && (
          <a href={p.contact_website} target="_blank" rel="noreferrer" className="flex items-center gap-1 underline">
            <Globe2 className="w-3 h-3" /> Website <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </article>
  );
}

export default function PublishersDirectory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/publishers"); setRows(data); }
      catch { /* show empty state */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--bf-bg)" }} data-testid="publishers-directory">
      <SiteNav />
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
        <div className="label-eyebrow mb-3">Bookforge directory</div>
        <h1 className="font-serif text-5xl mb-3">Publishers</h1>
        <p className="text-base max-w-2xl mb-10" style={{ color: "var(--bf-text-muted)" }}>
          Independent publishers vetted by the Bookforge team. Browse their services, charges, and terms — then send your manuscript directly from the Publish tab inside any of your books.
        </p>

        {loading ? (
          <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>Loading…</p>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center rounded-lg" style={{ background: "white", border: "1px dashed var(--bf-border)" }} data-testid="publishers-empty">
            <Building2 className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--bf-accent)" }} />
            <h3 className="font-serif text-2xl mb-2">No publishers approved yet</h3>
            <p className="text-sm mb-4" style={{ color: "var(--bf-text-muted)" }}>Our team is reviewing applications. Check back soon.</p>
            <a href="/register" className="text-sm underline" style={{ color: "var(--bf-primary)" }}>Are you a publisher? Apply →</a>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="publishers-grid">
            {rows.map((p) => <PublisherCard key={p.user_id} p={p} />)}
          </div>
        )}
      </main>
    </div>
  );
}
